import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/joy";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import { motion } from "framer-motion";
import { bookApi } from "../api";

export function PaymentSuccess() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking | paid | timeout
  const [orderData, setOrderData] = useState(null);

  const _search = window.location.search;
  const _isGift = !!new URLSearchParams(_search).get("Shp_gift");

  useEffect(() => {
    if (_isGift) return;   // сертификат обрабатывается на отдельной странице
    const params = new URLSearchParams(window.location.search);
    const urlOrderId = params.get("Shp_order");
    const orderId = localStorage.getItem("pending_order_id") || urlOrderId;
    const orderNumber = localStorage.getItem("pending_order_number");

    if (!orderId) {
      setStatus("paid");
      setOrderData({ order_number: orderNumber || "—" });
      return;
    }

    let attempts = 0;
    const maxAttempts = 15;
    const checkPayment = async () => {
      try {
        const res = await bookApi.checkPaymentStatus(orderId);
        if (res.paid) {
          setOrderData(res);
          setStatus("paid");
          localStorage.removeItem("pending_order_id");
          localStorage.removeItem("pending_order_number");
          return;
        }
      } catch (e) {
        console.warn("Payment check error:", e);
      }
      attempts++;
      if (attempts < maxAttempts) {
        setTimeout(checkPayment, 2000);
      } else {
        setOrderData({ order_number: orderNumber || "—", order_id: orderId });
        setStatus("timeout");
        localStorage.removeItem("pending_order_id");
        localStorage.removeItem("pending_order_number");
      }
    };
    checkPayment();
  }, []);

  const handleClose = () => { navigate("/", { replace: true }); };

  // Оплата сертификата → на отдельную страницу (все хуки уже вызваны выше)
  if (_isGift) {
    return <Navigate to={`/gift-success${_search}`} replace />;
  }

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#f5f5f5", p: 2 }}>
      <Box sx={{ maxWidth: 440, width: "100%", bgcolor: "background.surface", borderRadius: "24px", boxShadow: "0 12px 40px rgba(0,0,0,0.1)", p: 4, textAlign: "center" }}>
        {status === "checking" && (
          <Stack alignItems="center" gap={2.5} sx={{ py: 4 }}>
            <CircularProgress size="lg" />
            <Typography level="title-lg" fontWeight={700}>Проверяем оплату...</Typography>
            <Typography level="body-sm" color="neutral">Обычно это занимает несколько секунд</Typography>
          </Stack>
        )}

        {(status === "paid" || status === "timeout") && (
          <Stack alignItems="center" gap={2}>
            <motion.div initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 400, damping: 22 }}>
              <Box sx={{ width: 80, height: 80, borderRadius: "50%", mx: "auto", bgcolor: "success.100", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <CheckCircleRoundedIcon sx={{ fontSize: 44, color: "success.500" }} />
              </Box>
            </motion.div>

            <motion.div initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }} style={{ textAlign: "center" }}>
              <Typography level="title-lg" fontWeight={800}>Благодарим вас за доверие и заказ!</Typography>
              {orderData?.order_number && (
                <Typography level="body-sm" color="neutral" sx={{ mt: 1 }}>
                  Ваш заказ <strong>#{orderData.order_number}</strong> оплачен
                </Typography>
              )}
              {orderData?.total_price && (
                <Typography level="body-sm" sx={{ mt: 0.5 }}>
                  Сумма: <strong>{parseFloat(orderData.total_price).toLocaleString("ru-RU")} ₽</strong>
                </Typography>
              )}
              <Stack spacing={1.25} sx={{ mt: 2, maxWidth: 360, mx: "auto", textAlign: "left" }}>
                <Typography level="body-sm" color="neutral">
                  Вам на указанную при оформлении заказа почту, в течение 20 минут,
                  должно прийти уведомление с номером заказа.
                </Typography>
                <Typography level="body-sm" color="neutral">
                  На нашем сайте вы можете ввести номер заказа и узнать стадию вашего
                  заказа в разделе «Статус заказа».
                </Typography>
                <Typography level="body-sm" color="neutral">
                  Фотокнигу изготавливаем в течение 7 рабочих дней и передаём в СДЭК.
                </Typography>
                <Typography level="body-sm" color="neutral" sx={{ mt: 0.5, fontWeight: 600 }}>
                  С большим уважением, команда Magic Board!
                </Typography>
              </Stack>

              {status === "timeout" && (
                <Typography level="body-xs" sx={{ mt: 1.5, color: "warning.600" }}>
                  Если деньги были списаны — не переживайте, заказ будет обработан.
                </Typography>
              )}
            </motion.div>

            <motion.div initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.4 }} style={{ width: "100%" }}>
              <Button component={'a'} href={'https://photobook.magicboard.shop/'} fullWidth size="lg" variant="soft" color="success"
                onClick={handleClose} sx={{ mt: 2, borderRadius: "12px", fontWeight: 700 }}>
                Проверить статус
              </Button>
            </motion.div>
            <motion.div initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.4 }} style={{ width: "100%" }}>
              <Button fullWidth size="lg" color="neutral" onClick={handleClose} sx={{ borderRadius: "12px", fontWeight: 700 }}>
                Вернуться в конструктор
              </Button>
            </motion.div>
          </Stack>
        )}
      </Box>
    </Box>
  );
}


export function PaymentFail() {
  const navigate = useNavigate();

  const _search = window.location.search;
  if (new URLSearchParams(_search).get("Shp_gift")) {
    return <Navigate to={`/gift-fail${_search}`} replace />;
  }

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#f5f5f5", p: 2 }}>
      <Box sx={{ maxWidth: 440, width: "100%", bgcolor: "background.surface", borderRadius: "24px", boxShadow: "0 12px 40px rgba(0,0,0,0.1)", p: 4, textAlign: "center" }}>
        <Stack alignItems="center" gap={2}>
          <motion.div initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 400, damping: 22 }}>
            <Box sx={{ width: 80, height: 80, borderRadius: "50%", mx: "auto", bgcolor: "danger.50", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <ErrorRoundedIcon sx={{ fontSize: 44, color: "danger.500" }} />
            </Box>
          </motion.div>
          <motion.div initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }} style={{ textAlign: "center" }}>
            <Typography level="title-lg" fontWeight={800}>Оплата не завершена</Typography>
            <Typography level="body-sm" color="neutral" sx={{ mt: 1, maxWidth: 320, mx: "auto" }}>
              Платёж был отменён или произошла ошибка. Деньги не были списаны — вы можете попробовать снова.
            </Typography>
          </motion.div>
          <motion.div initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.4 }} style={{ width: "100%", display: "flex", flexDirection: "column", gap: 8 }}>
            <Button fullWidth size="lg" onClick={() => navigate("/", { replace: true })} sx={{ mt: 2, borderRadius: "12px", fontWeight: 700 }}>
              Вернуться в конструктор
            </Button>
          </motion.div>
        </Stack>
      </Box>
    </Box>
  );
}

// ─── Страница успеха: покупка подарочного сертификата ─────────────────────────
export function GiftPaymentSuccess() {
  const params = new URLSearchParams(window.location.search);
  const amount = params.get("OutSum");
  const SITE_URL = "https://photobook.magicboard.shop/";   // ← ваш сайт/Тильда при желании

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#f5f5f5", p: 2 }}>
      <Box sx={{ maxWidth: 440, width: "100%", bgcolor: "background.surface", borderRadius: "24px", boxShadow: "0 12px 40px rgba(0,0,0,0.1)", p: 4, textAlign: "center" }}>
        <Stack alignItems="center" gap={2}>
          <motion.div initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 400, damping: 22 }}>
            <Box sx={{ width: 80, height: 80, borderRadius: "50%", mx: "auto", bgcolor: "success.100", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <CheckCircleRoundedIcon sx={{ fontSize: 44, color: "success.500" }} />
            </Box>
          </motion.div>

          <motion.div initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }} style={{ textAlign: "center" }}>
            <Typography level="title-lg" fontWeight={800}>Сертификат оплачен!</Typography>
            {amount && (
              <Typography level="body-sm" sx={{ mt: 1 }}>
                Сумма: <strong>{parseFloat(amount).toLocaleString("ru-RU")} ₽</strong>
              </Typography>
            )}
            <Stack spacing={1.25} sx={{ mt: 2, maxWidth: 360, mx: "auto", textAlign: "left" }}>
              <Typography level="body-sm" color="neutral">
                Спасибо за покупку! Электронный сертификат с кодом отправлен на почту получателя,
                а вам на почту — подтверждение. Письма приходят в течение нескольких минут
                (если нет — проверьте папку «Спам»).
              </Typography>
              <Typography level="body-sm" color="neutral">
                <strong>Как активировать:</strong> получатель заходит на наш сайт, собирает
                фотокнигу нужного формата и вводит код сертификата при оформлении заказа.
              </Typography>
              <Typography level="body-sm" color="neutral">
                Если доставка была оплачена — получателю она достанется бесплатно, в любой город.
              </Typography>
              <Typography level="body-sm" color="neutral" sx={{ mt: 0.5, fontWeight: 600 }}>
                С большим уважением, команда Magic Board!
              </Typography>
            </Stack>
          </motion.div>

          <motion.div initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.4 }} style={{ width: "100%" }}>
            <Button component="a" href={SITE_URL} fullWidth size="lg" variant="soft" color="success"
              sx={{ mt: 2, borderRadius: "12px", fontWeight: 700 }}>
              Вернуться на сайт
            </Button>
          </motion.div>
        </Stack>
      </Box>
    </Box>
  );
}

// ─── Страница ошибки: покупка подарочного сертификата ─────────────────────────
export function GiftPaymentFail() {
  const SITE_URL = "https://photobook.magicboard.shop/";   // ← ваш сайт/Тильда при желании

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#f5f5f5", p: 2 }}>
      <Box sx={{ maxWidth: 440, width: "100%", bgcolor: "background.surface", borderRadius: "24px", boxShadow: "0 12px 40px rgba(0,0,0,0.1)", p: 4, textAlign: "center" }}>
        <Stack alignItems="center" gap={2}>
          <motion.div initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 400, damping: 22 }}>
            <Box sx={{ width: 80, height: 80, borderRadius: "50%", mx: "auto", bgcolor: "danger.50", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <ErrorRoundedIcon sx={{ fontSize: 44, color: "danger.500" }} />
            </Box>
          </motion.div>
          <motion.div initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }} style={{ textAlign: "center" }}>
            <Typography level="title-lg" fontWeight={800}>Оплата сертификата не завершена</Typography>
            <Typography level="body-sm" color="neutral" sx={{ mt: 1, maxWidth: 320, mx: "auto" }}>
              Оплата была отменена или произошла ошибка. Деньги не были списаны — вы можете оформить покупку сертификата ещё раз.
            </Typography>
          </motion.div>
          <motion.div initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.4 }} style={{ width: "100%" }}>
            <Button component="a" href={SITE_URL} fullWidth size="lg" sx={{ mt: 2, borderRadius: "12px", fontWeight: 700 }}>
              Вернуться на сайт
            </Button>
          </motion.div>
        </Stack>
      </Box>
    </Box>
  );
}